<template>
  <component
    :is="disableLink ? 'div' : 'a'"
    class="product-card"
    :class="{
      [$class('loading')]: !product,
      'product-card--studioshot': product?.tags?.includes('studioshot'),
      'product-card--no-image': hideImage,
    }"
    :href="disableLink ? null : productUrl"
    :aria-label="product?.title || null"
    :data-qa-product-type="productType !== '' ? productType : null"
    @click="!disableLink && trackProductSelect('PDP')"
  >
    <div v-if="hideImage" class="product-card__backdrop" aria-hidden="true" />

    <div
      v-else
      class="product-card__image-container"
      :class="{
        'product-card__image-container-banner': showFeaturedBanner,
      }"
    >
      <div
        class="product-card__image-main"
        :class="{
          masked: product && quickAddActive && !showFeaturedBanner,
        }"
      >
        <responsive-image
          aria-hidden="true"
          class="product-card__primary-image"
          :image="getFittingVariantWrapper(product).image ?? featuredImage"
          :lazyload="lazyload"
          :ratio="minimal ? '1:1' : $variable('product.imageRatio')"
          sizes="(min-width: 1024px) 25vw, 50vw"
        />

        <div v-if="!minimal" class="product-card__image-meta">
          <product-badges
            class="product-card__badges"
            :discount-preview="discountPreview"
            :product="product"
            template="card"
            :variant="localVariant"
          />

          <wishlist-button
            class="product-card__wishlist"
            :product="product"
            template="card"
            :variant="localVariant"
            :location="location"
          />
        </div>
      </div>

      <div
        v-if="showFeaturedBanner && !minimal"
        aria-hidden="true"
        class="product-card__featured-banner"
        :class="{
          masked: product && quickAddActive && showFeaturedBanner,
        }"
      >
        <responsive-image
          aria-hidden="true"
          class="product-card__featured-banner-image"
          :image="product.featuredBanner"
          :lazyload="lazyload"
          :padding="false"
          :ratio="false"
          sizes="(min-width: 1024px) 25vw, 50vw"
        />
      </div>
      <quick-add
        v-if="quickAddActive"
        class="product-card__quick-add"
        :campaign-card="isCampaign"
        :product="product"
        :location="quickAddLocation"
        :variant="variant"
        :loading="!product"
        :use-product-state="!isVariant"
        @track-click="trackProductSelect('Quick Add To Cart')"
      />
    </div>

    <div class="product-card__footer">
      <div
        v-if="isFlavour && !minimal && !hideImage"
        class="product-card__flavour-wrapper"
        :class="{ 'product-card__flavour-wrapper--grid': isVariant }"
      >
        <p
          v-if="isVariant"
          class="product-card__variant-title product-card__flavour-count text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          aria-hidden="true"
          v-text="flavourName"
        />

        <p
          v-if="
            (!isVariant || variantsShowCount) &&
            ((optionsCount > 1 && flavourCountText) || !product)
          "
          class="product-card__flavour-count text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          aria-hidden="true"
          v-text="flavourCountText"
        />
      </div>

      <p
        class="product-card__title"
        :class="titleClasses"
        aria-hidden="true"
        v-text="customTitle || product.title"
      />

      <p
        v-if="customSubtitle || product.subtitle || !product"
        class="product-card__subtitle text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        aria-hidden="true"
        v-html="customSubtitle || product.subtitle"
      />

      <star-rating-popup
        v-if="!minimal && !hideImage"
        class="product-card__reviews"
        :loading="!product"
        :product="product"
      />

      <product-prices
        v-if="!minimal"
        aria-hidden="true"
        class="product-card__prices"
        :compare-at-price="product?.priceObject?.compareAtPrice"
        :base-price="localVariant?.price?.amount"
        :discount-logic="discountLogic"
        :price="product?.priceObject?.price"
        template="card"
        :varies="product?.priceObject?.varies"
        :unit-price="localVariant?.unitPrice?.amount"
        :unit-price-measurement="localVariant?.unitPriceMeasurement"
        hide-unit-price-when-discounted
        :is-gift-card="product.giftCard"
        :unit-no-braces="unitPriceNoBraces"
      />
    </div>

    <quick-add
      v-if="hideImage && quickAddActive"
      class="product-card__quick-add"
      :campaign-card="isCampaign"
      :product="product"
      :location="quickAddLocation"
      :variant="variant"
      :loading="!product"
      :use-product-state="!isVariant"
      @track-click="trackProductSelect('Quick Add To Cart')"
    />
  </component>
</template>

<script>
/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean} [discountPreview] - Show preview of promoted campaign in
 * product card badges and prices.
 * @param {String} [displayBanner] - Display featured banner.
 * @param {Boolean} [lazyload] - Lazy load image.
 * @param {Boolean} [isVariant] - Product card represents a selected variant.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean|Object} variant - Specified or default variant.
 * @param {Boolean} minimal - Render minimal component.
 * @param {Boolean} [hideImage] - Hide the product image, moves quick add next
 * to the price (e.g. product family cards).
 * @param {Boolean|String} [customTitle] - Override product title.
 * @param {Boolean|String} [customSubtitle] - Override product subtitle, supports HTML.
 * @param {Boolean} [disableLink] - Render as a non-interactive wrapper
 * instead of a link to the product page (e.g. product family cards).
 * @param {Boolean} [unitPriceNoBraces] - Show unit price without brackets.
 *
 */
import { mapActions, mapState } from 'pinia';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useCartStore from '~stores/cart';

import ProductBadges from '~global/product-badges/product-badges';
import ProductPrices from '~global/product-prices/product-prices';
import ResponsiveImage from '~global/images/responsive-image';
import StarRatingPopup from '~global/star-rating/star-rating-popup';
import WishlistButton from '~async/wishlist-button/wishlist-button';
import QuickAdd from '~async/quick-add/quick-add';

import { getSelectedOrFirstAvailableVariant } from '~/format/format-product';
import { formatTrackingItem } from '~/format/format-tracking-item';
import { computeDiscountLogic } from '~/helpers/vue';
import { getFittingVariant } from '~/helpers/product-information';

export default {
  name: 'ProductCard',

  components: {
    ProductBadges,
    ProductPrices,
    QuickAdd,
    ResponsiveImage,
    StarRatingPopup,
    WishlistButton,
  },

  inject: {
    variantsShowCount: {
      from: 'variantsShowCount',
      default: true,
    },
  },

  props: {
    discountPreview: {
      type: Boolean,
      default: false,
    },
    displayBanner: {
      type: Boolean,
      default: false,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    isVariant: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    location: {
      type: String,
      default: 'Collection Page',
    },
    quickAddLocation: {
      type: String,
      default: 'Quick Add To Cart',
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    minimal: {
      type: Boolean,
      default: false,
    },
    hideImage: {
      type: Boolean,
      default: false,
    },
    disableLink: {
      type: Boolean,
      default: false,
    },
    customTitle: {
      type: [Boolean, String],
      default: false,
    },
    customSubtitle: {
      type: [Boolean, String],
      default: false,
    },
    isCampaign: {
      type: Boolean,
      default: false,
    },
    unitPriceNoBraces: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),

    /**
     * Hides or shows banner, depending on settings
     */
    showFeaturedBanner() {
      if (!this.displayBanner || !this.product.featuredBanner) {
        return false;
      }

      const isHiddenCampaignPage =
        globalThis.location.pathname.includes('campaign') &&
        this.product?.disableFeaturedBannerCampaign;
      const isHiddenOnCollection =
        this.product.disableFeaturedBannerCollections?.includes(
          cnvs.collection?.collectionId,
        );

      return !isHiddenCampaignPage && !isHiddenOnCollection;
    },

    /**
     * Compute discount logic.
     * @returns {Object}
     */
    discountLogic() {
      return computeDiscountLogic({
        discountPreview: this.discountPreview,
        onSale: this.localVariant?.compareAtPriceDiscount,
        product: this.product,
        variant: this.localVariant,
      });
    },

    /**
     * Checks whether to use variant first image.
     * - Use variant featured image (grey background) in product grid.
     * @returns {Object}
     */
    featuredImage() {
      if (this.localVariant?.localizedImage) {
        return this.localVariant.localizedImage;
      }

      if (this.localVariant?.featuredImage) {
        return this.localVariant.featuredImage;
      }

      return this.product.featuredImage;
    },

    /**
     * Compute flavour count text.
     * @returns {String}
     */
    flavourCountText() {
      if (!this.product || !this.optionsCount || !this.isFlavour) {
        return '';
      }

      if (this.isVariant) {
        return `+ ${this.optionsCount - 1}`;
      }

      return this.$string('product.product_card.option_count', {
        pluralise: this.optionsCount,
        replace: { count: this.optionsCount },
      });
    },

    /**
     * Compute flavour name.
     * @returns {String}
     */
    flavourName() {
      if (
        !this.product ||
        !this.localVariant?.selectedOptions?.length ||
        !this.isFlavour
      ) {
        return '';
      }

      const option = this.localVariant?.selectedOptions.find(
        (selectedOption) => {
          return this.$variable('product.flavourOptions').includes(
            selectedOption.name.toLowerCase(),
          );
        },
      );

      return option?.value;
    },

    /**
     * Checks whether options have flavours.
     * @returns {Boolean}
     */
    isFlavour() {
      if (!this.product) {
        return true;
      }

      return this.product.options?.find((option) => {
        return this.$variable('product.flavourOptions').includes(
          option.name.toLowerCase(),
        );
      });
    },

    /**
     * Compute local variant.
     * - Used to calculate selectedOrFirstAvailableVariant when data is passed
     *   using Liquid formatter as it's not efficient for Liquid to calculate.
     * - Uses same function as format product JS.
     * @returns {Object}
     */
    localVariant() {
      if (this.variant) {
        return this.variant;
      }

      if (this.product.selectedOrFirstAvailableVariant) {
        return this.product.selectedOrFirstAvailableVariant;
      }

      return getSelectedOrFirstAvailableVariant(this.product);
    },

    /**
     * Compute options count.
     * - Exclude options where all associated variants are 'hidden'.
     * @returns {Number}
     */
    optionsCount() {
      if (!this.product?.options?.length) {
        return 1;
      }

      const options = this.product.options[0].values.filter((value) => {
        return this.product.variants
          .filter((variant) => variant.selectedOptions[0].value === value)
          .some((variant) => variant.availableState !== 'hidden');
      });

      return options.length ?? 0;
    },

    /**
     * Compute title classes.
     * @returns {String}
     */
    titleClasses() {
      if (this.minimal) {
        return `
          text-mobile-headlines-h4
          text-desktop-headlines-h4
        `;
      }

      return `
        text-mobile-paragraph-m-semibold
        text-desktop-headlines-h6
      `;
    },

    /**
     * Check if product type is allowed for quick add. (e.g. not gift card)
     * @returns {boolean}
     */
    quickAddActive() {
      return !this.product.giftCard && this.product.type !== 'bundle';
    },

    /**
     * Set link of product card. Also check if we have a fitting variant for the search query.
     * @returns {boolean}
     */
    productUrl() {
      if (this.searchQuery !== '') {
        return (
          this.getFittingVariantWrapper(this.product).url ?? this.product.url
        );
      }
      if (this.isVariant) {
        return this.localVariant?.url;
      }
      return this.product?.url;
    },

    productType() {
      if (this.product?.type === 'bundle') {
        return 'bundle';
      }
      if (this.product?.type === 'giftcard') {
        return 'giftcard';
      }
      return '';
    },
  },

  methods: {
    ...mapActions(useCartStore, {
      getCartAttributeCode: 'getCartAttributeCode',
    }),
    /**
     * Track selected product from list view
     * - Need to send collection title, id and the product list
     */
    trackProductSelect(eventCategory) {
      const selectedVariant = this.product.selectedOrFirstAvailableVariant
        ? this.product.selectedOrFirstAvailableVariant
        : this.product.variants[0];
      const discountLogic = this.discountLogic;
      const campaignDiscount = this.getCampaignDiscount(
        discountLogic,
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        index: 0,
        product: this.product,
        variant: selectedVariant,
        collection: this.collection,
        template: this.template,
        discountObject,
        deliveryTime,
        currencyCode,
      });

      cnvs.EventBus.emit('dataLayer:select_item', {
        event: 'select_item',
        eventCategory,
        items: [item],
      });
    },

    /**
     * Just used in template 'search'.
     * Check if product has a available variant where the title matches the query.
     * - If so, use Image und URL of this variant. For display/product page link in search results.
     * @param {Object} product - Formatted product object.
     * @returns {Object} - Image and URL of fitting variant.
     */
    getFittingVariantWrapper(product) {
      if (!product || !this.searchQuery) {
        return {
          image: null,
          url: null,
        };
      }

      return getFittingVariant(product, this.searchQuery);
    },
  },
};
</script>
